import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import CreditInquiryApi from "../../../../../../infrastructure/api/creditInquiry";
import { LOAN_GUARANTEE_CHEQUE_INTENT } from "../../customerRequest/loanGuaranteeChequeIntentConstants";
import {
  OPERATOR_CARD_SX,
  OPERATOR_META_LABEL_SX,
} from "./operatorReviewStyles";

const CREDIT_STATUS = {
  pending: 0,
  running: 1,
  approved: 2,
  rejected: 3,
  error: 4,
};

const CREDIT_SUBJECT = {
  applicant: 0,
  guarantor: 1,
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const resolveAttemptStatus = (attempt, summaryFlags) => {
  const raw = attempt?.status ?? attempt?.Status;
  if (raw !== undefined && raw !== null && raw !== "") return Number(raw);
  if (summaryFlags?.isApproved) return CREDIT_STATUS.approved;
  if (summaryFlags?.isRejected) return CREDIT_STATUS.rejected;
  if (summaryFlags?.isPending) return CREDIT_STATUS.pending;
  return null;
};

const statusChipProps = (status) => {
  switch (status) {
    case CREDIT_STATUS.approved:
      return { label: "تأیید", color: "success" };
    case CREDIT_STATUS.rejected:
      return { label: "رد", color: "error" };
    case CREDIT_STATUS.running:
      return { label: "در حال پردازش", color: "info" };
    case CREDIT_STATUS.error:
      return { label: "خطا", color: "warning" };
    case CREDIT_STATUS.pending:
      return { label: "در انتظار", color: "default" };
    default:
      return { label: "ثبت نشده", color: "default" };
  }
};

const SubjectSection = ({
  title,
  attempt,
  isRequired,
  isEditable,
  retrying,
  loading,
  onRetry,
  skipMessage,
}) => {
  const status = resolveAttemptStatus(attempt, null);
  const chip = statusChipProps(status);
  const latestTitle = pick(attempt, "externalStatusTitle", "ExternalStatusTitle");
  const rejectionReasons = pick(attempt, "rejectionReasons", "RejectionReasons", "errorMessage", "ErrorMessage");
  const attemptNumber = pick(attempt, "attemptNumber", "AttemptNumber");
  const subjectName = pick(attempt, "subjectFullName", "SubjectFullName");

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={700}>
          {title}
          {isRequired ? " (الزامی)" : ""}
        </Typography>
        <Chip size="small" color={chip.color} label={chip.label} />
      </Stack>

      {skipMessage ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          {skipMessage}
        </Alert>
      ) : null}

      {attempt ? (
        <Stack spacing={0.75} sx={{ mb: 1 }}>
          {subjectName ? (
            <Typography variant="body2">
              <Typography component="span" sx={OPERATOR_META_LABEL_SX}>
                نام:
              </Typography>{" "}
              {subjectName}
            </Typography>
          ) : null}
          {latestTitle ? (
            <Typography variant="body2">
              <Typography component="span" sx={OPERATOR_META_LABEL_SX}>
                وضعیت پرتال:
              </Typography>{" "}
              {latestTitle}
            </Typography>
          ) : null}
          {attemptNumber ? (
            <Typography variant="body2" color="text.secondary">
              تلاش شماره {attemptNumber}
            </Typography>
          ) : null}
        </Stack>
      ) : !skipMessage ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          هنوز استعلامی ثبت نشده یا در حال اجراست.
        </Alert>
      ) : null}

      {rejectionReasons ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {rejectionReasons}
          </Typography>
        </Alert>
      ) : null}

      {isEditable && isRequired && onRetry ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            retrying ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
          }
          disabled={retrying || loading || status === CREDIT_STATUS.running}
          onClick={onRetry}
        >
          {retrying ? "در حال استعلام..." : "استعلام مجدد"}
        </Button>
      ) : null}
    </Box>
  );
};

SubjectSection.propTypes = {
  title: PropTypes.string.isRequired,
  attempt: PropTypes.object,
  isRequired: PropTypes.bool,
  isEditable: PropTypes.bool,
  retrying: PropTypes.bool,
  loading: PropTypes.bool,
  onRetry: PropTypes.func,
  skipMessage: PropTypes.string,
};

const CreditInquiryCard = ({
  requestInfoId,
  apiPath,
  needsLoan = false,
  loanGuaranteeChequeIntent,
  isEditable = false,
  refreshToken = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [retryingApplicant, setRetryingApplicant] = useState(false);
  const [retryingGuarantor, setRetryingGuarantor] = useState(false);
  const [summary, setSummary] = useState(null);

  const intent = String(loanGuaranteeChequeIntent ?? "");
  const isGuarantorIntent =
    intent === LOAN_GUARANTEE_CHEQUE_INTENT.GuarantorWithSayadCheque;

  const loadSummary = useCallback(async () => {
    if (!needsLoan || !requestInfoId || !apiPath) {
      setSummary(null);
      return;
    }

    setLoading(true);
    try {
      const data = await CreditInquiryApi.getSummary(apiPath, requestInfoId);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [apiPath, needsLoan, requestInfoId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshToken]);

  const requiredSubject = Number(
    summary?.requiredApprovedSubject ?? summary?.RequiredApprovedSubject ?? CREDIT_SUBJECT.applicant
  );

  const applicantAttempt =
    summary?.applicantLatestAttempt ?? summary?.ApplicantLatestAttempt ?? null;
  const guarantorAttempt =
    summary?.guarantorLatestAttempt ?? summary?.GuarantorLatestAttempt ?? null;

  const overallStatus = useMemo(
    () =>
      resolveAttemptStatus(
        requiredSubject === CREDIT_SUBJECT.guarantor
          ? guarantorAttempt ?? summary?.latestAttempt ?? summary?.LatestAttempt
          : applicantAttempt ?? summary?.latestAttempt ?? summary?.LatestAttempt,
        {
          isApproved: summary?.isApproved ?? summary?.IsApproved,
          isRejected: summary?.isRejected ?? summary?.IsRejected,
          isPending: summary?.isPending ?? summary?.IsPending,
        }
      ),
    [applicantAttempt, guarantorAttempt, requiredSubject, summary]
  );

  const chip = statusChipProps(overallStatus);
  const trackingCode = pick(summary, "iriaTrackingCode", "IriaTrackingCode");
  const requiredSubjectTitle = pick(
    summary,
    "requiredApprovedSubjectTitle",
    "RequiredApprovedSubjectTitle"
  );

  const handleRetryApplicant = async () => {
    if (!isEditable || !requestInfoId) return;

    setRetryingApplicant(true);
    try {
      const res = await CreditInquiryApi.retry(apiPath, requestInfoId);
      if (res?.isSuccess === false) {
        throw new Error(res?.message || res?.Message || "استعلام مجدد ناموفق بود.");
      }
      toast.success(res?.message || res?.Message || "استعلام مجدد ثبت شد.");
      await loadSummary();
    } catch (err) {
      toast.error(err?.message || "استعلام مجدد ناموفق بود.");
    } finally {
      setRetryingApplicant(false);
    }
  };

  const handleRetryGuarantor = async () => {
    if (!isEditable || !requestInfoId) return;

    setRetryingGuarantor(true);
    try {
      const res = await CreditInquiryApi.retryGuarantor(apiPath, requestInfoId);
      if (res?.isSuccess === false) {
        throw new Error(res?.message || res?.Message || "استعلام مجدد ضامن ناموفق بود.");
      }
      toast.success(res?.message || res?.Message || "استعلام مجدد ضامن ثبت شد.");
      await loadSummary();
    } catch (err) {
      toast.error(err?.message || "استعلام مجدد ضامن ناموفق بود.");
    } finally {
      setRetryingGuarantor(false);
    }
  };

  if (!needsLoan) return null;

  return (
    <Box sx={{ ...OPERATOR_CARD_SX, p: { xs: 2, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <CreditScoreOutlinedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={800}>
          استعلام اعتباری (لندتک)
        </Typography>
        <Chip size="small" color={chip.color} label={chip.label} sx={{ mr: "auto" }} />
        {loading ? <CircularProgress size={18} /> : null}
      </Stack>

      {requiredSubjectTitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          موضوع الزامی برای بازدید: {requiredSubjectTitle}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          پس از ثبت درخواست، استعلام به‌صورت خودکار در پس‌زمینه انجام می‌شود.
          نتیجه منفی باعث حذف پرونده نمی‌شود.
        </Typography>
      )}

      {trackingCode ? (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <Typography component="span" sx={OPERATOR_META_LABEL_SX}>
            کد پیگیری ایریا:
          </Typography>{" "}
          {trackingCode}
        </Typography>
      ) : null}

      {isGuarantorIntent ? (
        <>
          <SubjectSection
            title="استعلام متقاضی"
            attempt={applicantAttempt}
            isRequired={false}
            skipMessage="برای این درخواست استعلام متقاضی در ثبت‌نام انجام نمی‌شود."
          />
          <Divider sx={{ my: 1 }} />
          <SubjectSection
            title="استعلام ضامن"
            attempt={guarantorAttempt}
            isRequired
            isEditable={isEditable}
            retrying={retryingGuarantor}
            loading={loading}
            onRetry={handleRetryGuarantor}
          />
        </>
      ) : (
        <SubjectSection
          title="استعلام متقاضی"
          attempt={applicantAttempt ?? summary?.latestAttempt ?? summary?.LatestAttempt}
          isRequired
          isEditable={isEditable}
          retrying={retryingApplicant}
          loading={loading}
          onRetry={handleRetryApplicant}
        />
      )}
    </Box>
  );
};

CreditInquiryCard.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiPath: PropTypes.string,
  needsLoan: PropTypes.bool,
  loanGuaranteeChequeIntent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  isEditable: PropTypes.bool,
  refreshToken: PropTypes.number,
};

export default CreditInquiryCard;
